import {
  CALL_RING_TIMEOUT_MS,
  type CallDeviceInfo,
  type CallMedia,
  type CallQualityReport,
  type GroupCallDto,
  type GroupCallSignal,
  type IceServersDto,
  type ServerEvent,
  type UserSummaryDto,
} from '@dijitalapp/shared';
import { api, ApiError } from './api';
import { audioStats, CALL_AUDIO_KICK_EVENT } from './calls';
import { thisDevice } from './device';
import { closeLocal, flashTitle, holdSoundsForCall, lookingElsewhere, ringtone, showLocal } from './notify';
import type { RealtimeClient } from './realtime';

/*
 * Group calls (up to 6): one RTCPeerConnection per other person, all directly between devices (mesh). Whoever
 * joins sends an offer to each person already in; those answer. A data channel per person carries their
 * mute and camera state. Set-up goes over the realtime socket ("gcall.signal"); who is in comes from the API.
 */

export interface GroupPeer {
  user: UserSummaryDto;
  stream: MediaStream | null;
  state: 'connecting' | 'connected' | 'failed';
  muted: boolean;
  cameraOff: boolean;
  /** Showing their screen (in the video line, instead of the camera). */
  sharing: boolean;
  /** Their voice alone (one stream per connection, set once on its audio element). */
  audio: MediaStream | null;
  /** What they are on, when their app said. */
  device: CallDeviceInfo | null;
}

export interface GroupCallView {
  callId: string;
  conversationId: string;
  title: string;
  media: CallMedia;
  phase: 'ringing' | 'joining' | 'active' | 'ended';
  startedBy: UserSummaryDto;
  startedAt: number;
  localStream: MediaStream | null;
  muted: boolean;
  cameraOff: boolean;
  facing: 'user' | 'environment';
  /** My shared screen, window or tab (sent instead of the camera to everyone). */
  screenStream: MediaStream | null;
  peers: GroupPeer[];
  error: string | null;
}

interface Link {
  pc: RTCPeerConnection;
  channel: RTCDataChannel;
  pendingIce: RTCIceCandidateInit[];
  stream: MediaStream;
  /** I sent the offer: I am the one who restarts this connection when its path is lost. */
  offer: boolean;
  connected: boolean;
  disconnectTimer: ReturnType<typeof setTimeout> | null;
  lastRestart: number;
  audioIn: { value: number; at: number };
}

const PING_MS = 15_000;
const WATCHDOG_MS = 2000;
const NO_AUDIO_MS = 5000;
const RESTART_GAP_MS = 10_000;

export class GroupCallManager {
  view: GroupCallView | null = null;
  /** Running call per group, for the "join" bar in that chat. */
  open = new Map<string, GroupCallDto>();
  private links = new Map<string, Link>();
  private listeners = new Set<() => void>();
  private ice: IceServersDto | null = null;
  private ping: ReturnType<typeof setInterval> | null = null;
  private ringer: { stop: () => void } | null = null;
  private stopAlert: (() => void) | null = null;
  private ringTimer: ReturnType<typeof setTimeout> | null = null;
  private clearTimer: ReturnType<typeof setTimeout> | null = null;
  /** Set by the app: my id, and whether a 1:1 call is running (then a group call does not ring). */
  me = '';
  otherCallActive: () => boolean = () => false;
  text = { ringing: (group: string, name: string) => `${name} · ${group}`, tapToJoin: '' };

  private watchdog: ReturnType<typeof setInterval> | null = null;
  private holding = false;
  private restarts = 0;
  private noAudioSeen = false;
  private connectedAt: number | null = null;
  private snapshots = new Map<string, Awaited<ReturnType<typeof audioStats>>['snapshot']>();
  private micPending = false;

  constructor(private readonly rt: RealtimeClient) {
    window.addEventListener('pagehide', () => {
      // Closing the tab mid-call: leave at once instead of waiting for the sweep.
      if (this.view && this.view.phase !== 'ringing' && this.view.phase !== 'ended') navigator.sendBeacon?.(`/api/v1/group-calls/${this.view.callId}/leave`);
    });
    // Wi-Fi to mobile data and back: every connection I offered gets a fresh path at once.
    const changed = () => {
      if (this.view?.phase !== 'active') return;
      for (const l of this.links.values()) this.restart(l, true);
    };
    window.addEventListener('online', changed);
    (navigator as Navigator & { connection?: EventTarget }).connection?.addEventListener?.('change', changed);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible' || this.view?.phase !== 'active') return;
      const mic = this.view.localStream?.getAudioTracks()[0];
      if (this.micPending || mic?.readyState === 'ended') void this.reacquireMic();
    });
  }

  /** ICE restart of one connection, from the side that offered it (the other side answers the new offer). */
  private restart(link: Link, now = false) {
    if (!link.offer || link.pc.signalingState === 'closed') return;
    const t = Date.now();
    if (!now && t - link.lastRestart < RESTART_GAP_MS) return;
    link.lastRestart = t;
    this.restarts++;
    link.pc.restartIce();
  }

  /** The microphone stopped (iPhone background, another app took the sound): a new one on every connection. */
  private watchMic(track: MediaStreamTrack) {
    track.addEventListener('ended', () => {
      if (this.view?.localStream?.getAudioTracks()[0] !== track || this.view.phase === 'ended') return;
      if (document.visibilityState === 'visible') void this.reacquireMic();
      else this.micPending = true;
    });
  }

  private async reacquireMic(): Promise<void> {
    this.micPending = false;
    const v = this.view;
    if (!v?.localStream || v.phase !== 'active') return;
    try {
      const fresh = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      const track = fresh.getAudioTracks()[0];
      if (!track) return;
      if (this.view?.callId !== v.callId || this.view.phase !== 'active' || !this.view.localStream) {
        track.stop();
        return;
      }
      track.enabled = !this.view.muted;
      this.watchMic(track);
      for (const l of this.links.values()) {
        const sender = l.pc.getSenders().find((s) => s.track?.kind === 'audio');
        await sender?.replaceTrack(track).catch(() => undefined);
      }
      this.view.localStream.getAudioTracks().forEach((t) => t.stop());
      this.update({ localStream: new MediaStream([track, ...this.view.localStream.getVideoTracks()]) });
    } catch {
      this.micPending = true;
    }
  }

  /** Every WATCHDOG_MS: is each person's voice coming in? A stuck connection I offered is restarted. */
  private async check(): Promise<void> {
    if (this.view?.phase !== 'active') return;
    const now = Date.now();
    for (const [userId, link] of this.links) {
      if (!link.connected) continue;
      try {
        const r = await audioStats(link.pc);
        this.snapshots.set(userId, r.snapshot);
        if (r.inPackets !== link.audioIn.value) link.audioIn = { value: r.inPackets, at: now };
        const peer = this.view?.peers.find((p) => p.user.id === userId);
        if (peer?.muted) link.audioIn.at = now;
        if (now - link.audioIn.at >= NO_AUDIO_MS) {
          this.noAudioSeen = true;
          this.restart(link);
          window.dispatchEvent(new Event(CALL_AUDIO_KICK_EVENT));
        }
      } catch {
        /* statistics are best effort */
      }
    }
    const mic = this.view?.localStream?.getAudioTracks()[0];
    if (mic?.readyState === 'ended' && document.visibilityState === 'visible') void this.reacquireMic();
  }

  /** What this device measured over the group call, summed over its connections (sent once on leaving). */
  private sendQuality(callId: string) {
    const list = [...this.snapshots.values()];
    if (!callId || !list.length || !this.connectedAt) return;
    const max = (k: 'rttMs' | 'jitterMs') => list.reduce<number | null>((m, s) => (s[k] == null ? m : Math.max(m ?? 0, s[k]!)), null);
    const sum = (k: 'packetsLost' | 'audioPacketsReceived' | 'audioBytesSent') => list.reduce((n, s) => n + (s[k] ?? 0), 0);
    const relay = list.find((s) => s.candidateType === 'relay');
    const first = relay ?? list[0]!;
    const body: CallQualityReport = {
      candidateType: first.candidateType ?? null,
      protocol: first.protocol ?? null,
      rttMs: max('rttMs'),
      jitterMs: max('jitterMs'),
      packetsLost: sum('packetsLost'),
      audioPacketsReceived: sum('audioPacketsReceived'),
      audioBytesSent: sum('audioBytesSent'),
      iceRestarts: Math.min(1000, this.restarts),
      noAudioDetected: this.noAudioSeen,
      device: thisDevice(),
      durationMs: Math.max(0, Date.now() - this.connectedAt),
    };
    void api('POST', `/calls/${callId}/quality`, body).catch(() => undefined);
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  private emit() {
    this.listeners.forEach((l) => l());
  }
  private update(patch: Partial<GroupCallView>) {
    if (!this.view) return;
    this.view = { ...this.view, ...patch };
    this.emit();
  }
  private setPeer(userId: string, patch: Partial<GroupPeer>) {
    if (!this.view) return;
    this.update({ peers: this.view.peers.map((p) => (p.user.id === userId ? { ...p, ...patch } : p)) });
  }

  get busy(): boolean {
    return this.view !== null && this.view.phase !== 'ended' && this.view.phase !== 'ringing';
  }

  /** Calls this device knows of, asked again (after a reconnect: some may have ended meanwhile). */
  refreshKnown(): void {
    for (const id of [...this.open.keys()]) void this.refresh(id);
  }

  /** The group's running call (asked when a group chat opens). */
  async refresh(conversationId: string): Promise<void> {
    try {
      const { call } = await api<{ call: GroupCallDto | null }>('GET', `/conversations/${conversationId}/group-call`);
      if (call) this.open.set(conversationId, call);
      else this.open.delete(conversationId);
      this.emit();
    } catch {
      /* not a member any more, or offline */
    }
  }

  /** Starts the group's call, or joins the one running. */
  async join(conversationId: string, title: string, media: CallMedia, carry?: { muted: boolean; cameraOff: boolean }): Promise<void> {
    if (this.busy) return;
    this.stopRinging();
    const running = this.open.get(conversationId);
    const callMedia = running?.media ?? media;
    this.view = {
      callId: running?.id ?? '',
      conversationId,
      title,
      media: callMedia,
      phase: 'joining',
      startedBy: running?.startedBy ?? { id: this.me, displayName: '', username: '' } as UserSummaryDto,
      startedAt: running ? Date.parse(running.startedAt) : Date.now(),
      localStream: null,
      muted: carry?.muted ?? false,
      cameraOff: carry?.cameraOff ?? false,
      facing: 'user',
      screenStream: null,
      peers: [],
      error: null,
    };
    this.emit();
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: callMedia === 'video' ? { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 24 } } : false,
      });
    } catch {
      return this.end('permission');
    }
    // A call carried on from a 1:1 call keeps the microphone and camera as they were.
    stream.getAudioTracks().forEach((t) => {
      t.enabled = !this.view?.muted;
      this.watchMic(t);
    });
    stream.getVideoTracks().forEach((t) => (t.enabled = !this.view?.cameraOff));
    if (!this.holding) {
      this.holding = true;
      holdSoundsForCall(true);
    }
    this.update({ localStream: stream });
    try {
      this.ice = await api<IceServersDto>('GET', '/calls/ice-servers');
      const r = await api<{ call: GroupCallDto; started: boolean }>('POST', `/conversations/${conversationId}/group-call`, { media: callMedia, device: thisDevice() });
      if (!this.view) return stream.getTracks().forEach((t) => t.stop());
      this.open.set(conversationId, r.call);
      this.update({ callId: r.call.id, media: r.call.media, startedBy: r.call.startedBy, startedAt: Date.parse(r.call.startedAt), phase: 'active' });
      // I am the newcomer: I call everyone already in.
      for (const p of r.call.participants) if (p.id !== this.me) void this.connect(p, true);
      this.ping = setInterval(() => {
        if (this.view?.callId) void api('POST', `/group-calls/${this.view.callId}/ping`).catch(() => undefined);
      }, PING_MS);
      this.watchdog ??= setInterval(() => void this.check(), WATCHDOG_MS);
    } catch (err) {
      this.end(err instanceof ApiError ? err.code : 'failed');
    }
  }

  private peerOf(user: UserSummaryDto): GroupPeer {
    const device = this.view ? (this.open.get(this.view.conversationId)?.devices?.[user.id] ?? null) : null;
    return { user, stream: null, state: 'connecting', muted: false, cameraOff: this.view?.media !== 'video', sharing: false, audio: null, device };
  }

  /** Opens the connection to one person; `offer` when I am the one who joined. */
  private async connect(user: UserSummaryDto, offer: boolean): Promise<Link | null> {
    const view = this.view;
    if (!view || !view.localStream || this.links.has(user.id)) return this.links.get(user.id) ?? null;
    if (!view.peers.some((p) => p.user.id === user.id)) this.update({ peers: [...view.peers, this.peerOf(user)] });
    const pc = new RTCPeerConnection({ iceServers: this.ice?.iceServers ?? [], iceTransportPolicy: this.ice?.relayOnly ? 'relay' : 'all' });
    const stream = new MediaStream();
    const channel = pc.createDataChannel('state', { negotiated: true, id: 0 });
    const link: Link = { pc, channel, pendingIce: [], stream, offer, connected: false, disconnectTimer: null, lastRestart: 0, audioIn: { value: 0, at: Date.now() } };
    this.links.set(user.id, link);
    const callId = view.callId;
    const local = view.localStream;
    local.getTracks().forEach((t) => pc.addTrack(t, local));
    // A video line in voice calls too (from whoever offers), so a screen can be shared without renegotiating.
    if (offer && local.getVideoTracks().length === 0) pc.addTransceiver('video', { direction: 'sendrecv', streams: [local] });
    if (view.screenStream) void this.videoSender(pc)?.replaceTrack(view.screenStream.getVideoTracks()[0] ?? null).catch(() => undefined);
    pc.onicecandidate = (e) => {
      if (e.candidate) this.signal(callId, user.id, { type: 'ice', candidate: e.candidate.toJSON() as never });
    };
    pc.ontrack = (e) => {
      if (!stream.getTracks().includes(e.track)) stream.addTrack(e.track);
      // Their voice gets a stream of its own, made once: its audio element is never reset.
      const voice = e.track.kind === 'audio' ? { audio: new MediaStream([e.track]) } : {};
      this.setPeer(user.id, { stream: new MediaStream(stream.getTracks()), ...voice });
    };
    pc.onconnectionstatechange = () => {
      if (this.links.get(user.id)?.pc !== pc) return;
      if (pc.connectionState === 'connected') {
        link.connected = true;
        link.audioIn.at = Date.now();
        this.connectedAt ??= Date.now();
        this.setPeer(user.id, { state: 'connected' });
      }
      if (pc.connectionState === 'failed') {
        this.setPeer(user.id, { state: 'failed' });
        // Another try from my side with fresh candidates.
        this.restart(link, true);
      }
    };
    pc.oniceconnectionstatechange = () => {
      if (this.links.get(user.id)?.pc !== pc) return;
      if (pc.iceConnectionState === 'disconnected') {
        this.setPeer(user.id, { state: 'connecting' });
        if (link.disconnectTimer) clearTimeout(link.disconnectTimer);
        link.disconnectTimer = setTimeout(() => {
          if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') this.restart(link, true);
        }, 3000);
      } else if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        if (link.disconnectTimer) clearTimeout(link.disconnectTimer);
        link.disconnectTimer = null;
        this.setPeer(user.id, { state: 'connected' });
      }
    };
    pc.onnegotiationneeded = async () => {
      if (!offer) return;
      try {
        await pc.setLocalDescription(await pc.createOffer());
        this.signal(callId, user.id, { type: 'offer', sdp: pc.localDescription?.sdp ?? '' });
      } catch {
        this.setPeer(user.id, { state: 'failed' });
      }
    };
    channel.onopen = () => this.sendState();
    channel.onmessage = (e) => {
      try {
        const m = JSON.parse(String(e.data)) as { t?: string; muted?: boolean; cameraOff?: boolean; sharing?: boolean };
        if (m.t === 'state') this.setPeer(user.id, { muted: m.muted === true, cameraOff: m.cameraOff === true, sharing: m.sharing === true });
      } catch {
        /* not ours */
      }
    };
    return link;
  }

  private signal(callId: string, to: string, data: GroupCallSignal) {
    this.rt.command('gcall.signal', { callId, to, data });
  }

  private async onSignal(from: string, data: GroupCallSignal) {
    const view = this.view;
    if (!view || view.phase !== 'active') return;
    let link = this.links.get(from);
    if (data.type === 'offer') {
      if (!link) {
        const person = this.open.get(view.conversationId)?.participants.find((p) => p.id === from) ?? ({ id: from, displayName: '…', username: '' } as UserSummaryDto);
        link = (await this.connect(person, false)) ?? undefined;
      }
      if (!link) return;
      await link.pc.setRemoteDescription({ type: 'offer', sdp: data.sdp });
      // The offer's video line (a voice call's spare one included) can carry my screen back.
      for (const t of link.pc.getTransceivers()) if (t.receiver.track.kind === 'video' && t.direction === 'recvonly') t.direction = 'sendrecv';
      const screen = this.view?.screenStream?.getVideoTracks()[0];
      if (screen) await this.videoSender(link.pc)?.replaceTrack(screen).catch(() => undefined);
      await link.pc.setLocalDescription(await link.pc.createAnswer());
      this.signal(view.callId, from, { type: 'answer', sdp: link.pc.localDescription?.sdp ?? '' });
      await this.flush(link);
    } else if (data.type === 'answer' && link) {
      await link.pc.setRemoteDescription({ type: 'answer', sdp: data.sdp });
      await this.flush(link);
    } else if (data.type === 'ice' && link) {
      if (link.pc.remoteDescription) await link.pc.addIceCandidate(data.candidate).catch(() => undefined);
      else link.pendingIce.push(data.candidate);
    }
  }

  private async flush(link: Link) {
    for (const c of link.pendingIce.splice(0)) await link.pc.addIceCandidate(c).catch(() => undefined);
  }

  private drop(userId: string) {
    const link = this.links.get(userId);
    if (link?.disconnectTimer) clearTimeout(link.disconnectTimer);
    link?.pc.close();
    this.links.delete(userId);
    if (this.view) this.update({ peers: this.view.peers.filter((p) => p.user.id !== userId) });
  }

  private sendState() {
    const v = this.view;
    if (!v) return;
    const msg = JSON.stringify({ t: 'state', muted: v.muted, cameraOff: v.cameraOff || v.media !== 'video', sharing: Boolean(v.screenStream) });
    for (const l of this.links.values()) if (l.channel.readyState === 'open') l.channel.send(msg);
  }

  toggleMute(): void {
    if (!this.view) return;
    const muted = !this.view.muted;
    this.view.localStream?.getAudioTracks().forEach((t) => (t.enabled = !muted));
    this.update({ muted });
    this.sendState();
  }

  toggleCamera(): void {
    if (!this.view || this.view.media !== 'video') return;
    const cameraOff = !this.view.cameraOff;
    this.view.localStream?.getVideoTracks().forEach((t) => (t.enabled = !cameraOff));
    this.update({ cameraOff });
    this.sendState();
  }

  /** Front and back camera on phones: the new picture replaces the old one on every connection. */
  async flipCamera(): Promise<void> {
    const v = this.view;
    if (!v?.localStream || v.media !== 'video') return;
    const facing = v.facing === 'user' ? 'environment' : 'user';
    try {
      const fresh = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing, width: { ideal: 640 }, height: { ideal: 480 } } });
      const track = fresh.getVideoTracks()[0]!;
      track.enabled = !v.cameraOff;
      // While sharing, the others keep seeing the screen; the new camera is used when sharing stops.
      if (!v.screenStream) for (const l of this.links.values()) await this.videoSender(l.pc)?.replaceTrack(track);
      const old = v.localStream.getVideoTracks()[0];
      old?.stop();
      const stream = new MediaStream([...v.localStream.getAudioTracks(), track]);
      this.update({ localStream: stream, facing });
    } catch {
      /* only one camera */
    }
  }

  private videoSender(pc: RTCPeerConnection): RTCRtpSender | null {
    return pc.getTransceivers().find((t) => t.receiver.track.kind === 'video' && t.currentDirection !== 'stopped')?.sender ?? null;
  }

  /** Shares my screen, a window or a tab with everyone in the call, instead of my camera (voice calls too). */
  async startScreenShare(): Promise<void> {
    const v = this.view;
    if (!v || v.phase !== 'active' || v.screenStream || typeof navigator.mediaDevices?.getDisplayMedia !== 'function') return;
    const display = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: { ideal: 15, max: 30 }, width: { max: 1920 }, height: { max: 1080 } },
      audio: false,
      // Chrome: never offer this very tab (an endless mirror).
      selfBrowserSurface: 'exclude',
      surfaceSwitching: 'include',
    } as DisplayMediaStreamOptions);
    const track = display.getVideoTracks()[0];
    if (!track || this.view?.callId !== v.callId || this.view.phase !== 'active') {
      display.getTracks().forEach((t) => t.stop());
      return;
    }
    // Text on a screen must stay sharp: lower the frame rate before the resolution.
    track.contentHint = 'detail';
    // The browser's own "stop sharing" bar ends it too.
    track.addEventListener('ended', () => void this.stopScreenShare());
    for (const l of this.links.values()) await this.videoSender(l.pc)?.replaceTrack(track).catch(() => undefined);
    this.update({ screenStream: display });
    this.sendState();
  }

  async stopScreenShare(): Promise<void> {
    const v = this.view;
    const screen = v?.screenStream;
    if (!v || !screen) return;
    screen.getTracks().forEach((t) => t.stop());
    this.update({ screenStream: null });
    // Back to the camera (on or off as it was), or nothing in a voice call.
    const camera = v.localStream?.getVideoTracks()[0] ?? null;
    for (const l of this.links.values()) await this.videoSender(l.pc)?.replaceTrack(camera).catch(() => undefined);
    this.sendState();
  }

  leave(): void {
    const v = this.view;
    if (!v) return;
    if (v.phase === 'ringing') return this.decline();
    if (v.callId) void api('POST', `/group-calls/${v.callId}/leave`).catch(() => undefined);
    this.end(null);
  }

  decline(): void {
    this.stopRinging();
    this.view = null;
    this.emit();
  }

  dismiss(): void {
    if (this.view?.phase === 'ended') {
      this.view = null;
      this.emit();
    }
  }

  private end(error: string | null) {
    if (this.view && this.view.phase === 'active') this.sendQuality(this.view.callId);
    if (this.ping) clearInterval(this.ping);
    this.ping = null;
    if (this.watchdog) clearInterval(this.watchdog);
    this.watchdog = null;
    this.snapshots.clear();
    this.restarts = 0;
    this.noAudioSeen = false;
    this.connectedAt = null;
    this.micPending = false;
    if (this.holding) {
      this.holding = false;
      holdSoundsForCall(false);
    }
    for (const id of [...this.links.keys()]) this.drop(id);
    this.view?.localStream?.getTracks().forEach((t) => t.stop());
    this.view?.screenStream?.getTracks().forEach((t) => t.stop());
    this.stopRinging();
    if (!this.view) return;
    if (!error) {
      this.view = null;
      this.emit();
      return;
    }
    this.update({ phase: 'ended', error, localStream: null, screenStream: null, peers: [] });
    if (this.clearTimer) clearTimeout(this.clearTimer);
    this.clearTimer = setTimeout(() => this.dismiss(), 5000);
  }

  private startRinging(call: GroupCallDto, title: string) {
    this.ringer = ringtone('groupCall');
    const line = this.text.ringing(title, call.startedBy.displayName);
    this.stopAlert = flashTitle(`📞 ${line}`);
    if (lookingElsewhere(null, null)) void showLocal(line, { body: this.text.tapToJoin, tag: `gcall-${call.id}`, url: `/messages/${call.conversationId}`, requireInteraction: true });
    this.ringTimer = setTimeout(() => {
      if (this.view?.phase === 'ringing' && this.view.callId === call.id) this.decline();
    }, CALL_RING_TIMEOUT_MS);
  }

  private stopRinging() {
    if (this.ringTimer) clearTimeout(this.ringTimer);
    this.ringTimer = null;
    this.ringer?.stop();
    this.ringer = null;
    this.stopAlert?.();
    this.stopAlert = null;
    if (this.view?.callId) void closeLocal(`gcall-${this.view.callId}`);
  }

  /**
   * Server events routed here by the chat store; `titleOf` names a group for the ring, `rings` says whether this
   * group may ring (not muted, notifications not "mentions only" or off; the push worker applies the same rule).
   * A group that may not ring still shows its call to join.
   */
  handle(e: ServerEvent, titleOf: (conversationId: string) => string, rings?: (conversationId: string) => boolean): void {
    switch (e.event) {
      case 'gcall.ringing': {
        const call = e.payload.call;
        this.open.set(call.conversationId, call);
        this.emit();
        if (this.view || this.otherCallActive() || call.startedBy.id === this.me) return;
        if (rings && !rings(call.conversationId)) return;
        const title = titleOf(call.conversationId);
        this.view = {
          callId: call.id,
          conversationId: call.conversationId,
          title,
          media: call.media,
          phase: 'ringing',
          startedBy: call.startedBy,
          startedAt: Date.parse(call.startedAt),
          localStream: null,
          muted: false,
          cameraOff: false,
          facing: 'user',
          screenStream: null,
          peers: [],
          error: null,
        };
        this.emit();
        this.startRinging(call, title);
        return;
      }
      case 'gcall.updated':
        if (e.payload.call) this.open.set(e.payload.conversationId, e.payload.call);
        else this.open.delete(e.payload.conversationId);
        // Nobody left in the call that rings here: stop ringing.
        if (!e.payload.call && this.view?.phase === 'ringing' && this.view.conversationId === e.payload.conversationId) this.decline();
        // Names of people who joined, for tiles waiting on their offer.
        if (e.payload.call && this.view && this.view.callId === e.payload.call.id) {
          const byId = new Map(e.payload.call.participants.map((p) => [p.id, p]));
          const devices = e.payload.call.devices ?? {};
          this.update({ peers: this.view.peers.map((p) => ({ ...p, user: byId.get(p.user.id) ?? p.user, device: devices[p.user.id] ?? p.device })) });
        }
        this.emit();
        return;
      case 'gcall.joined':
        // They will send me an offer; show their tile already.
        if (this.view?.callId === e.payload.callId && !this.view.peers.some((p) => p.user.id === e.payload.user.id)) {
          this.update({ peers: [...this.view.peers, this.peerOf(e.payload.user)] });
        }
        return;
      case 'gcall.left':
        if (this.view?.callId === e.payload.callId) this.drop(e.payload.userId);
        return;
      case 'gcall.signal':
        if (this.view?.callId === e.payload.callId) void this.onSignal(e.payload.from, e.payload.data).catch(() => this.setPeer(e.payload.from, { state: 'failed' }));
        return;
      default:
        return;
    }
  }
}
